import type { EntityAction } from "@rebasepro/cms-types";
import { client } from "../lib/rebaseClient";

/**
 * The review workflow, on the card itself.
 *
 * Approving is an ordinary field write — it is a human moving a card, which is
 * exactly what the design says the approval step is. Sending is NOT: it goes
 * through `/api/functions/outreach/:id/send`, the only path that delivers, so
 * every gate (admin auth, approved-only, suppression re-check, daily cap,
 * delivery configured) applies no matter where the click came from. An action
 * that wrote `status: "sent"` directly would look identical here and quietly
 * bypass all of them.
 *
 * Send confirms first, and names the recipient in the prompt. It is the one
 * irreversible thing this app can do, and the address is the fact worth
 * re-reading before it happens.
 */

const statusOf = (entity: unknown): string =>
    String((entity as { values?: Record<string, unknown> })?.values?.status ?? "");

const valueOf = (entity: unknown, key: string): string =>
    String((entity as { values?: Record<string, unknown> })?.values?.[key] ?? "");

export const approveEmailAction: EntityAction = {
    key: "approve_email",
    name: "Approve to send",
    icon: "CheckCircle",
    showActionsInListView: true,
    isEnabled: ({ entity }) => statusOf(entity) === "needs_review",
    onClick: async ({ entity, context }) => {
        if (!entity?.id) return;
        try {
            await client.data.collection("emails").update(entity.id, { status: "approved" });
            context?.snackbarController?.open({ type: "success", message: "Approved. It still has to be sent by hand." });
        } catch (err) {
            context?.snackbarController?.open({
                type: "error",
                message: `Could not approve: ${err instanceof Error ? err.message : String(err)}`
            });
        }
    }
};

export const rejectEmailAction: EntityAction = {
    key: "reject_email",
    name: "Reject",
    icon: "Ban",
    showActionsInListView: true,
    isEnabled: ({ entity }) => ["needs_review", "approved"].includes(statusOf(entity)),
    onClick: async ({ entity, context }) => {
        if (!entity?.id) return;
        try {
            await client.data.collection("emails").update(entity.id, { status: "rejected" });
            context?.snackbarController?.open({ type: "info", message: "Rejected. The person stays in People — nothing is deleted." });
        } catch (err) {
            context?.snackbarController?.open({
                type: "error",
                message: `Could not reject: ${err instanceof Error ? err.message : String(err)}`
            });
        }
    }
};

export const sendEmailAction: EntityAction = {
    key: "send_email",
    name: "Send it",
    icon: "Send",
    // Not on the list row. Sending an email you have not opened is the one
    // click this app should never make easy; the composer that opens with the
    // row has Send under the message, where it is read first.
    showActionsInListView: false,
    // Deliberately only enabled on an already-approved row. The server enforces
    // this too — this just stops the click that was always going to 409.
    isEnabled: ({ entity }) => statusOf(entity) === "approved",
    onClick: async ({ entity, context }) => {
        if (!entity?.id) return;
        const to = valueOf(entity, "to_email");
        const subject = valueOf(entity, "subject");

        const confirmed = window.confirm(
            `Send this email to ${to}?\n\n"${subject}"\n\n` +
            "This delivers a real message to a real person and cannot be undone."
        );
        if (!confirmed) return;

        try {
            // Through the client, not a hand-rolled fetch: it resolves the base
            // URL, injects the JWT, and retries once on 401. A raw fetch here
            // would need all three re-implemented, and would break the moment
            // the API moves off the page's own origin.
            await client.functions.invoke("outreach", undefined, {
                method: "POST",
                path: `/${entity.id}/send`
            });
            context?.snackbarController?.open({ type: "success", message: `Sent to ${to}.` });
        } catch (err) {
            // The endpoint's refusals are written to be read by a person — it
            // lists exactly which env vars are missing, or why the row was
            // refused. Surfacing the server's own wording beats paraphrasing it
            // into something vaguer.
            const api = err as { body?: { error?: string; problems?: string[] }; message?: string };
            const problems = Array.isArray(api.body?.problems) ? `\n\n${api.body!.problems!.join("\n")}` : "";
            context?.snackbarController?.open({
                type: "error",
                message: `${api.body?.error ?? api.message ?? "Send failed"}${problems}`
            });
        }
    }
};

export const emailActions: EntityAction[] = [approveEmailAction, rejectEmailAction, sendEmailAction];
